import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import API_BASE_URL, { API_BASE_URL_WITHOUT_API } from '../config/api';
import './SuggestedDestinations.css';

const SuggestedDestinations = () => {
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [togglingStatus, setTogglingStatus] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    fetchDestinations();
  }, []);

  const fetchDestinations = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/suggested-destinations`);
      setDestinations(response.data.data || []);
      setLoading(false);
    } catch (error) {
      setError('Failed to fetch suggested destinations');
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this destination?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/admin/suggested-destinations/${id}`);
      setDestinations(destinations.filter(dest => dest._id !== id));
    } catch (error) {
      alert('Failed to delete destination');
    }
  };

  const handleToggleActive = async (destinationId, currentStatus) => {
    const newStatus = !currentStatus;
    setTogglingStatus(prev => ({ ...prev, [destinationId]: true }));
    
    try {
      const response = await axios.put(`${API_BASE_URL}/admin/suggested-destinations/${destinationId}`, {
        isActive: newStatus
      });
      
      // Update the destination in the local state
      setDestinations(destinations.map(destination => 
        destination._id === destinationId 
          ? { ...destination, isActive: response.data.data.isActive }
          : destination
      ));
    } catch (error) {
      setError('Failed to update destination status');
    } finally {
      setTogglingStatus(prev => ({ ...prev, [destinationId]: false }));
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container">
          <div className="destinations-header">
            <h1>Suggested Destinations</h1>
            <div className="skeleton skeleton-button" style={{ width: '200px', height: '40px', borderRadius: '8px' }}></div>
          </div>
          <div className="destinations-table-container">
            <table className="destinations-table">
              <thead>
                <tr>
                  <th>Logo</th>
                  <th>City</th>
                  <th>Location</th>
                  <th>Coordinates</th>
                  <th>Order</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5, 6].map((index) => (
                  <tr key={index} className="skeleton-row">
                    <td>
                      <div className="skeleton skeleton-image" style={{ width: '60px', height: '60px', borderRadius: '8px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '80%', height: '16px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '70%', height: '14px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '120px', height: '14px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '50px', height: '14px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-switch" style={{ width: '100px', height: '26px' }}></div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <div className="skeleton skeleton-button" style={{ width: '36px', height: '36px', borderRadius: '8px' }}></div>
                        <div className="skeleton skeleton-button" style={{ width: '36px', height: '36px', borderRadius: '8px' }}></div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container">
        <div className="destinations-header">
          <h1>Suggested Destinations</h1>
          <Link to="/sy-admin/suggested-destinations/new" className="btn btn-primary">
            Add New Destination
          </Link>
        </div>

        {error && <div className="error-message">{error}</div>}

        {destinations.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📍</div>
            <h2>No destinations found</h2>
            <p>No suggested destinations match your search criteria.</p>
            <Link to="/sy-admin/suggested-destinations/new" className="btn btn-primary" style={{ marginTop: '16px' }}>
              Add Your First Destination
            </Link>
          </div>
        ) : (
          <div className="destinations-table-container">
            <table className="destinations-table">
              <thead>
                <tr>
                  <th>Logo</th>
                  <th>City</th>
                  <th>Location</th>
                  <th>Coordinates</th>
                  <th>Order</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {destinations.map((destination) => {
                  const isTogglingStatus = togglingStatus[destination._id];
                  return (
                    <tr key={destination._id}>
                      <td>
                        <div className="destination-logo-cell">
                          {destination.logo ? (
                            <img
                              src={`${API_BASE_URL_WITHOUT_API}${destination.logo}`}
                              alt={destination.city}
                              className="destination-table-logo"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div className="destination-logo-placeholder" style={{ display: destination.logo ? 'none' : 'flex' }}>
                            {destination.city.charAt(0).toUpperCase()}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="destination-city-cell">
                          <span className="destination-city-text">{destination.city}</span>
                        </div>
                      </td>
                      <td>
                        <div className="destination-location-cell">
                          <span>
                            {destination.state && `${destination.state}, `}
                            {destination.country}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="destination-coordinates-cell">
                          <span className="coordinates-text">
                            📍 {destination.cityLatitude}, {destination.cityLongitude}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="destination-order-cell">
                          <span className="order-badge">{destination.displayOrder}</span>
                        </div>
                      </td>
                      <td>
                        <div className="status-cell">
                          <label className="status-toggle-label">
                            <div className="toggle-switch">
                              <input
                                type="checkbox"
                                checked={destination.isActive}
                                onChange={() => handleToggleActive(destination._id, destination.isActive)}
                                disabled={isTogglingStatus}
                                className="toggle-input"
                              />
                              <span className={`toggle-slider ${isTogglingStatus ? 'loading' : ''}`}></span>
                            </div>
                            <span className={`status-text ${destination.isActive ? 'status-active-text' : 'status-inactive-text'}`}>
                              {destination.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </label>
                        </div>
                      </td>
                      <td>
                        <div className="table-actions">
                          <button
                            onClick={() => navigate(`/sy-admin/suggested-destinations/edit/${destination._id}`)}
                            className="btn btn-edit btn-icon-only"
                            title="Edit Destination"
                          >
                            <span className="btn-icon">✏️</span>
                          </button>
                          <button
                            onClick={() => handleDelete(destination._id)}
                            className="btn btn-delete btn-icon-only"
                            title="Delete Destination"
                          >
                            <span className="btn-icon">🗑️</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SuggestedDestinations;

